import type {
  AuthRole,
  LoginRequest,
  SessionPayload,
  TwoFactorRequest,
} from "@noverik/contracts";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiRequest } from "../lib/api";

type LoginRole = Extract<AuthRole, "client" | "agent">;

type AuthContextValue = {
  session: SessionPayload | null;
  loading: boolean;
  refresh: () => Promise<SessionPayload>;
  login: (role: LoginRole, credentials: LoginRequest) => Promise<SessionPayload>;
  verifyAgentTwoFactor: (token: string) => Promise<SessionPayload>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const next = await apiRequest<SessionPayload>("/session");
    setSession(next);
    return next;
  }, []);

  useEffect(() => {
    let active = true;

    apiRequest<SessionPayload>("/session")
      .then((next) => {
        if (active) {
          setSession(next);
        }
      })
      .catch(() => {
        if (active) {
          setSession(null);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (
    role: LoginRole,
    credentials: LoginRequest,
  ) => {
    if (!session?.csrfToken) {
      await refresh();
    }

    const csrfToken = session?.csrfToken ?? (await apiRequest<SessionPayload>("/session")).csrfToken;

    try {
      const next = await apiRequest<SessionPayload>(
        role === "agent" ? "/auth/agent/login" : "/auth/client/login",
        {
          method: "POST",
          csrfToken,
          body: credentials,
        },
      );
      setSession(next);
      return next;
    } catch (error) {
      await refresh().catch(() => undefined);
      throw error;
    }
  }, [refresh, session?.csrfToken]);

  const verifyAgentTwoFactor = useCallback(async (token: string) => {
    const current = session ?? await refresh();

    try {
      const next = await apiRequest<SessionPayload>("/auth/agent/2fa", {
        method: "POST",
        csrfToken: current.csrfToken,
        body: { token } satisfies TwoFactorRequest,
      });

      setSession(next);
      return next;
    } catch (error) {
      await refresh().catch(() => undefined);
      throw error;
    }
  }, [refresh, session]);

  const logout = useCallback(async () => {
    const current = session ?? await refresh();

    try {
      const next = await apiRequest<SessionPayload>("/auth/logout", {
        method: "POST",
        csrfToken: current.csrfToken,
      });
      setSession(next);
    } catch {
      setSession(null);
      await refresh().catch(() => undefined);
    }
  }, [refresh, session]);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    loading,
    refresh,
    login,
    verifyAgentTwoFactor,
    logout,
  }), [session, loading, refresh, login, verifyAgentTwoFactor, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }

  return value;
}
